import React from "react";
import { ord, fmt, standingOf, tableRows, compTitle, compSub, isLoaded, RESULT_ERROR } from "./resultsModel";

// Presentational pieces of the event results page (locked mockup v52, card
// 34022f7d), in the .oir design language (styles: eventResults.css, prefixed
// er- so they never meet the Oireachtas flow's own .pill/.tab/.chip rules).
//
// The results tables are CSS grids (pinned Place + Dancer, judges swipe on
// phones), so they carry full ARIA table semantics: table > row >
// columnheader/cell, with aria-colspan on the championship judge groups and
// on the "Did not place" divider.

const Msr = ({ children, className = "" }) => <span className={`msr ${className}`} aria-hidden="true">{children}</span>;

const initials = (name) =>
  String(name || "").split(/[\s-]+/).filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "?";

export const Avatar = ({ name, photo }) =>
  photo ? <img className="er-av" src={photo} alt="" /> : <span className="er-av" aria-hidden="true">{initials(name)}</span>;

// Place pill: medal + 1st/2nd/3rd (gold/silver/bronze), Not placed, Pending.
export const PlacePill = ({ standing, published = true }) => {
  if (!published) return <span className="er-place wait">Pending</span>;
  if (!standing || standing.state === "loading") return <span className="er-place wait">…</span>;
  if (standing.state === "error") return <span className="er-place wait">Not loaded</span>;
  if (standing.state === "none") return <span className="er-place np">No result</span>;
  if (standing.state === "unplaced") return <span className="er-place np">Not placed</span>;
  const p = standing.place;
  return (
    <span className={`er-place ${p <= 3 ? `p${p}` : ""}`}>
      <Msr>workspace_premium</Msr>
      {ord(p)}
    </span>
  );
};

const RowPlace = ({ row }) =>
  row.placed ? <PlacePill standing={{ state: "placed", place: row.place }} />
    : <span className="er-place plain"><Msr className="slot">workspace_premium</Msr>{ord(row.place)}</span>;

const Meta = ({ row }) => (
  <div className="er-meta" role="cell">
    {row.card != null && <span className="er-cardno">#{row.card}</span>}
    {row.card != null && row.school ? " · " : ""}
    {row.school}
  </div>
);

// last header cell: names the second-line cell (card number · school);
// absolutely positioned so it takes no grid slot
const MetaHead = () => <div role="columnheader" className="er-sr-only">Number and school</div>;

const rowClass = (row, mine, pick) =>
  ["er-rr", mine.has(row.dancerId) ? "me" : "", row.placed ? "" : "unplaced", pick ? "pick" : "", pick?.picked === row.dancerId ? "picked" : ""]
    .filter(Boolean).join(" ");

// My adjudications (card 930d3ac0, part 2): the judge's own column is
// highlighted, and a row opens their marks and comments for that dancer.
// pick: { picked, onPick } on that tab only.
const meCol = (col) => (col?.mine ? " me-col" : "");
const pickProps = (row, pick) =>
  pick
    ? {
      tabIndex: 0,
      "aria-expanded": pick.picked === row.dancerId,
      onClick: () => pick.onPick(row.dancerId),
      onKeyDown: (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          pick.onPick(row.dancerId);
        }
      },
    }
    : {};

// The judge's own marks for one dancer, under their row: place, points and
// each round's score, place and comment.
const MyMarks = ({ row, result, span }) => {
  const own = row.judges.filter((j) => j && j.mine);
  return (
    <div className="er-mymarks" role="row">
      <div role="cell" aria-colspan={span}>
        {own.length ? own.map((j) => (
          <div key={j.key} className="er-mm">
            <div className="er-mmh">
              <b>Your marks{j.round ? ` · Round ${j.round}` : ""}</b>
              {j.place ? <span>{ord(j.place)}</span> : null}
              {result.multi && j.place ? <span>{fmt(j.points)} pts</span> : null}
              {!result.multi || result.slot ? <span className="er-raw">{fmt(j.score)}</span> : null}
            </div>
            {result.rounds > 1 && !result.slot
              ? j.rounds.map((rd) => (
                <div key={rd.round} className="er-mmr">
                  <span className="er-rl">Round {rd.round}</span> <b>{ord(rd.place)}</b> <span className="er-raw">{fmt(rd.score)}</span>
                  <div className="er-jc">{rd.comment ? `“${rd.comment}”` : "No comment."}</div>
                </div>
              ))
              : <div className="er-jc">{j.comment || j.rounds[0]?.comment ? `“${j.comment || j.rounds[0]?.comment}”` : "No comment."}</div>}
          </div>
        )) : <div className="er-jc">You didn't mark this dancer.</div>}
      </div>
    </div>
  );
};

const Cutoff = ({ span }) => (
  <div className="er-cutoff" role="row">
    <span role="cell" aria-colspan={span}>Did not place</span>
  </div>
);

const headNote = (result, competed) => {
  const n = competed || result.rows.length;
  return result.fullField ? `${n} dancers · ${result.placerCount} placed` : `Top ${result.placerCount} of ${n} dancers`;
};

// Single judge: Place | Dancer | Score (raw score only, no points).
const SingleTable = ({ result, mine, competed, pick }) => {
  const { rows, cutAt } = tableRows(result);
  const judge = result.judges[0]?.name;
  return (
    <>
      <div className="er-rt-head">
        <span className="er-note">{headNote(result, competed)}</span>
        {judge && <span className="er-judge1">Judge: <b>{judge}</b></span>}
      </div>
      <div className="er-rt" role="table" aria-label={judge ? `Results, judge ${judge}` : "Results"}>
        <div className="er-rr th" role="row">
          <div role="columnheader">Place</div><div role="columnheader">Dancer</div><div role="columnheader" className="er-pts">Score</div><MetaHead />
        </div>
        {rows.map((r, i) => (
          <React.Fragment key={r.dancerId}>
            {i === cutAt && <Cutoff span={4} />}
            <div className={rowClass(r, mine, pick)} role="row" data-dancer={r.dancerId} {...pickProps(r, pick)}>
              <div role="cell"><RowPlace row={r} /></div>
              <div role="cell" className="er-nm">{r.name}</div>
              <div role="cell" className="er-pts">{fmt(r.score)}</div>
              <Meta row={r} />
            </div>
            {pick?.picked === r.dancerId && <MyMarks row={r} result={result} span={4} />}
          </React.Fragment>
        ))}
      </div>
    </>
  );
};

const judgeHeader = (j) => (j.round ? `${j.name} · R${j.round}` : j.name);

// Multi judge, one round each (3-judge one-round, and 6/9-judge slots):
// Place | Dancer | Points | a column per judge (place · points, raw score).
// Place + Dancer stay pinned while the judges scroll sideways on phones.
const JudgesTable = ({ result, mine, competed, pick }) => {
  const { rows, cutAt } = tableRows(result);
  const cols = 4 + result.judges.length;
  return (
    <>
      <div className="er-rt-head">
        <span className="er-note">{headNote(result, competed)}</span>
        <span className="er-scroll-hint">Swipe for judges<Msr>arrow_forward</Msr></span>
      </div>
      <div className="er-rt judges" style={{ "--j": result.judges.length }} role="table" aria-label="Results by judge">
        <div className="er-rr th" role="row">
          <div role="columnheader">Place</div><div role="columnheader">Dancer</div><div role="columnheader" className="er-pts">Points</div>
          {result.judges.map((j) => <div key={j.key} role="columnheader" className={`er-jd${meCol(j)}`}>{judgeHeader(j)}</div>)}
          <MetaHead />
        </div>
        {rows.map((r, i) => (
          <React.Fragment key={r.dancerId}>
            {i === cutAt && <Cutoff span={cols} />}
            <div className={rowClass(r, mine, pick)} role="row" data-dancer={r.dancerId} {...pickProps(r, pick)}>
              <div role="cell"><RowPlace row={r} /></div>
              <div role="cell" className="er-nm">{r.name}</div>
              <div role="cell" className="er-pts">{fmt(r.points)}</div>
              {result.judges.map((col, k) => {
                const x = r.judges[k];
                return (
                  <div key={col.key} role="cell" className={`er-jd${meCol(col)}`}>
                    {x && x.place ? <><b>{ord(x.place)}</b> · <b>{fmt(x.points)}</b> pts<span className="er-raw">{fmt(x.score)}</span></> : "–"}
                  </div>
                );
              })}
              <Meta row={r} />
            </div>
            {pick?.picked === r.dancerId && <MyMarks row={r} result={result} span={cols} />}
          </React.Fragment>
        ))}
      </div>
    </>
  );
};

// Championship (a judge scores every round): a column group per judge,
// R1..Rn (score, round place) + Total (judge place, points).
const RoundsTable = ({ result, mine, competed, pick }) => {
  const { rows, cutAt } = tableRows(result);
  const R = result.rounds;
  const J = result.judges.length;
  const cols = 4 + J * (R + 1);
  const style = { "--j": J, "--n": J * (R + 1) };
  return (
    <>
      <div className="er-rt-head">
        <span className="er-note">{headNote(result, competed)}</span>
        <span className="er-scroll-hint">Swipe for judges<Msr>arrow_forward</Msr></span>
      </div>
      <div className="er-rt judges rounds" style={style} role="table" aria-label="Results by judge and round">
        <div className="er-rr th th1" role="row">
          <div role="columnheader" aria-colspan={3} /><div aria-hidden="true" /><div aria-hidden="true" />
          {result.judges.map((j) => (
            <div key={j.key} role="columnheader" aria-colspan={R + 1} className={`er-jg${meCol(j)}`} style={{ gridColumn: `span ${R + 1}` }}>{j.name}</div>
          ))}
        </div>
        <div className="er-rr th" role="row">
          <div role="columnheader">Place</div><div role="columnheader">Dancer</div><div role="columnheader" className="er-pts">Points</div>
          {result.judges.map((j) => (
            <React.Fragment key={j.key}>
              {Array.from({ length: R }, (_, k) => (
                <div key={k} role="columnheader" aria-label={`${j.name}, round ${k + 1}`} className={`er-rd ${k === 0 ? "jstart" : ""}${meCol(j)}`}>R{k + 1}</div>
              ))}
              <div role="columnheader" aria-label={`${j.name}, total`} className={`er-rd tot${meCol(j)}`}>Total</div>
            </React.Fragment>
          ))}
          <MetaHead />
        </div>
        {rows.map((r, i) => (
          <React.Fragment key={r.dancerId}>
            {i === cutAt && <Cutoff span={cols} />}
            <div className={rowClass(r, mine, pick)} role="row" data-dancer={r.dancerId} {...pickProps(r, pick)}>
              <div role="cell"><RowPlace row={r} /></div>
              <div role="cell" className="er-nm">{r.name}</div>
              <div role="cell" className="er-pts">{fmt(r.points)}</div>
              {result.judges.map((col, jn) => {
                const x = r.judges[jn];
                return (
                  <React.Fragment key={col.key}>
                    {Array.from({ length: R }, (_, k) => {
                      const rd = x?.rounds?.[k];
                      return (
                        <div key={k} role="cell" className={`er-rd ${k === 0 ? "jstart" : ""}${meCol(col)}`}>
                          {rd ? <>{fmt(rd.score)}<small>{ord(rd.place)}</small></> : "–"}
                        </div>
                      );
                    })}
                    <div role="cell" className={`er-rd tot${meCol(col)}`}>{x?.place ? <><b>{ord(x.place)}</b><small>{fmt(x.points)} pts</small></> : "–"}</div>
                  </React.Fragment>
                );
              })}
              <Meta row={r} />
            </div>
            {pick?.picked === r.dancerId && <MyMarks row={r} result={result} span={cols} />}
          </React.Fragment>
        ))}
      </div>
    </>
  );
};

export const ResultTable = ({ result, mine = new Set(), competed, pick }) => {
  if (!result.rows.length) return <div className="er-empty">No results to show.</div>;
  if (!result.multi) return <SingleTable result={result} mine={mine} competed={competed} pick={pick} />;
  if (!result.slot && result.rounds > 1) return <RoundsTable result={result} mine={mine} competed={competed} pick={pick} />;
  return <JudgesTable result={result} mine={mine} competed={competed} pick={pick} />;
};

// A failed load, with a way to try again.
const LoadError = ({ onRetry }) => (
  <div className="er-empty" role="alert">
    These results could not be loaded.
    {onRetry && (
      <div><button type="button" className="er-btn er-retry" onClick={onRetry}><Msr>refresh</Msr>Try again</button></div>
    )}
  </div>
);

// The viewer's own dancer in one competition: place, points and every
// judge's card with each round's place and comment (comments reach the page
// only for the viewer's own dancers).
export const OwnDetail = ({ result, dancers, showName, onPrint, onRetry }) => (
  <>
    {dancers.map((d) => {
      const st = standingOf(result, d.id);
      if (st.state === "error") {
        return (
          <div key={d.id} className="er-mydetail none" role="alert">
            {d.name}'s own result could not be loaded (only the top places are shown).
            {onRetry && <div><button type="button" className="er-btn er-retry" onClick={onRetry}><Msr>refresh</Msr>Try again</button></div>}
          </div>
        );
      }
      if (st.state === "none") {
        return <div key={d.id} className="er-mydetail none">No result recorded for {d.name} in this competition.</div>;
      }
      if (st.state !== "placed" && st.state !== "unplaced") return null;
      const r = st.row;
      const perRound = result.rounds > 1 && !result.slot;
      return (
        <div key={d.id} className="er-mydetail" data-own={d.id}>
          <h4>
            {showName && <span>{d.name}</span>}
            <PlacePill standing={st} />
            {result.multi && <span className="er-pts-note">{fmt(r.points)} Irish points</span>}
          </h4>
          {result.partial && (
            // the placing is public; the judges' comments come only with the
            // signed-in read, which failed
            <div className="er-partial" role="alert">
              {d.name}'s judges' comments could not be loaded.
              {onRetry && <button type="button" className="er-btn er-retry" onClick={onRetry}><Msr>refresh</Msr>Try again</button>}
            </div>
          )}
          <div className="er-jgrid">
            {r.judges.map((j) => (
              <div key={j.key} className="er-jcard">
                <div className="er-jhead">
                  <Avatar name={j.name} photo={j.photo} />
                  <div className="er-jhw">
                    <div className="er-jn">{j.name}{j.round ? ` · Round ${j.round}` : ""}</div>
                    <div className="er-js">
                      {j.place ? <b>{ord(j.place)}</b> : "–"}
                      {result.multi && j.place ? <> · {fmt(j.points)} pts</> : null}
                      {!result.multi || result.slot ? <span className="er-raw">{fmt(j.score)}</span> : null}
                    </div>
                  </div>
                </div>
                {perRound
                  ? j.rounds.map((rd) => (
                    <div key={rd.round} className="er-jr">
                      <div className="er-jrh"><span className="er-rl">Round {rd.round}</span><b>{ord(rd.place)}</b><span className="er-raw">{fmt(rd.score)}</span></div>
                      {rd.comment && <div className="er-jc">“{rd.comment}”</div>}
                    </div>
                  ))
                  : (j.comment || j.rounds[0]?.comment) && <div className="er-jc">“{j.comment || j.rounds[0]?.comment}”</div>}
              </div>
            ))}
          </div>
          {onPrint && (
            <div className="er-mprint">
              <button type="button" className="er-btn" onClick={() => onPrint(d.id)}><Msr>print</Msr>Print results</button>
            </div>
          )}
        </div>
      );
    })}
  </>
);

const standingLabel = (st) =>
  st.state === "placed" ? ord(st.place)
    : st.state === "unplaced" ? "not placed"
      : st.state === "none" ? "no result"
        : st.state === "error" ? "not loaded" : "…";

// One competition in the All Results list.
export const CompetitionCard = ({ comp, result, open, onToggle, onRetry, view, mineHere, justPublished, onPrint }) => {
  const published = !!comp.results_published;
  const competed = comp.participatedDancers?.length;
  const loaded = isLoaded(result) ? result : null;
  return (
    <div
      id={`result-${comp._id}`}
      className={["er-comp", mineHere.length ? "has-mine" : "", open ? "open" : "", published ? "" : "pending"].filter(Boolean).join(" ")}
      data-comp={comp.code}
    >
      <button type="button" className="er-comp-h" onClick={published ? onToggle : undefined} disabled={!published} aria-expanded={open}>
        <span className="er-code">{comp.code}</span>
        <span className="er-name">{compTitle(comp)}<small>{compSub(comp)}</small></span>
        <span className="er-mine-slot">
          {view === "parent" && mineHere.map((d) => {
            const st = published ? standingOf(result, d.id) : null;
            return <span key={d.id} className={`er-pill mine ${st && st.state !== "placed" ? "np" : ""}`}>{d.name.split(" ")[0]}: {published ? standingLabel(st) : "pending"}</span>;
          })}
          {view === "teacher" && mineHere.length > 0 && <span className="er-pill mine">{mineHere.length} of your students</span>}
        </span>
        <span className="er-st">
          {published ? (justPublished && <span className="er-pill new">Just published</span>) : <span className="er-pill pending">Not yet published</span>}
        </span>
        <Msr className="er-chev">{published ? "expand_more" : ""}</Msr>
      </button>
      {open && published && (
        <div className="er-comp-b">
          {!result && <div className="er-empty">Loading results…</div>}
          {result === RESULT_ERROR && <LoadError onRetry={onRetry} />}
          {loaded && (
            <>
              {mineHere.length > 0 && <OwnDetail result={loaded} dancers={mineHere} showName onRetry={onRetry} onPrint={onPrint && ((id) => onPrint(comp._id, id))} />}
              <ResultTable result={loaded} mine={new Set(mineHere.map((d) => d.id))} competed={competed} />
            </>
          )}
        </div>
      )}
    </div>
  );
};

// My adjudications: one competition the judge marked. Open, it is the full
// table with their column highlighted; a dancer's row opens the judge's own
// marks and comments under it.
export const JudgedCard = ({ comp, result, open, onToggle, onRetry, picked, onPick }) => {
  const loaded = isLoaded(result) ? result : null;
  return (
    <div id={`judged-${comp._id}`} className={["er-comp", open ? "open" : ""].filter(Boolean).join(" ")} data-comp={comp.code}>
      <button type="button" className="er-comp-h" onClick={onToggle} aria-expanded={open}>
        <span className="er-code">{comp.code}</span>
        <span className="er-name">{compTitle(comp)}<small>{compSub(comp)}</small></span>
        <span className="er-mine-slot" />
        <span className="er-st" />
        <Msr className="er-chev">expand_more</Msr>
      </button>
      {open && (
        <div className="er-comp-b">
          {!result && <div className="er-empty">Loading results…</div>}
          {result === RESULT_ERROR && <LoadError onRetry={onRetry} />}
          {loaded && (
            <>
              <p className="er-pick-hint"><Msr>touch_app</Msr>Tap a dancer to see your marks and comments.</p>
              <ResultTable result={loaded} competed={comp.participatedDancers?.length} pick={{ picked, onPick }} />
            </>
          )}
        </div>
      )}
    </div>
  );
};

// My Results: one card per dancer (parent) or student (teacher), a row per
// competition entered; a published row opens in place.
export const DancerCards = ({ list, results, openKey, onToggle, onRetry, onPrint }) => (
  <div className="er-mine-grid">
    {list.map((d) => (
      <div key={d.id} className="er-mcard" data-dancer-card={d.id}>
        <h3>{d.name}</h3>
        {d.comps.map((c) => {
          const published = !!c.results_published;
          const result = results[c._id];
          const loaded = isLoaded(result) ? result : null;
          const key = `${d.id}|${c._id}`;
          const isOpen = openKey === key;
          return (
            <React.Fragment key={c._id}>
              <button
                type="button"
                className={`er-mrow ${isOpen ? "open" : ""}`}
                onClick={published ? () => onToggle(key, c._id) : undefined}
                disabled={!published}
                aria-expanded={isOpen}
              >
                <span className="er-code">{c.code}</span>
                <span className="er-mname">{compTitle(c)}<small>{compSub(c)}</small></span>
                <span className="er-mright">
                  <PlacePill published={published} standing={published ? standingOf(result, d.id) : null} />
                  {published && <Msr className="er-mchev">expand_more</Msr>}
                </span>
              </button>
              {isOpen && published && (
                <div className="er-mexp">
                  {!result && <div className="er-empty">Loading results…</div>}
                  {result === RESULT_ERROR && <LoadError onRetry={() => onRetry(c._id)} />}
                  {loaded && (
                    <>
                      <OwnDetail result={loaded} dancers={[d]} showName={false} onRetry={() => onRetry(c._id)} onPrint={onPrint && ((id) => onPrint(c._id, id))} />
                      <ResultTable result={loaded} mine={new Set([d.id])} competed={c.participatedDancers?.length} />
                    </>
                  )}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    ))}
  </div>
);
